<script setup lang="ts">
  import { ref, computed, onMounted, nextTick, watch, onBeforeUnmount } from 'vue'
  import {
    useRoute,
    useRouter,
    onBeforeRouteLeave,
    type RouteLocationNormalized,
    type NavigationGuardNext
  } from 'vue-router'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { v4 as uuidv4 } from 'uuid'
  import {
    FileText,
    Eye,
    Calendar,
    SquareCheck,
    PenTool,
    ArrowLeft,
    MoreHorizontal,
    Copy,
    Trash,
    ChevronLeft,
    ChevronRight,
    ZoomIn,
    ZoomOut,
    Type,
    User,
    Scale,
    Trash2,
    Pencil,
    Replace,
    AlignLeft
  } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import DropdownMenu from '@/components/ui/dropdown-menu/DropdownMenu.vue'
  import DropdownMenuContent from '@/components/ui/dropdown-menu/DropdownMenuContent.vue'
  import DropdownMenuItem from '@/components/ui/dropdown-menu/DropdownMenuItem.vue'
  import DropdownMenuTrigger from '@/components/ui/dropdown-menu/DropdownMenuTrigger.vue'
  import { VuePDF } from '@tato30/vue-pdf'
  import { usePDFWithCmaps as usePDF } from '@/composables/usePDFWithCmaps'
  import {
    PdfFormTemplatesService,
    CasesService,
    type GenericDataMapper,
    type PDFFormTemplate,
    type FormField,
    TagRelationshipEnum,
    FormFieldTypeEnum,
    AssignedToEnum,
    FieldTypeEnum
  } from '@/types/generated'
  import FormFieldOverlayItem from '@/components/forms/FormFieldOverlayItem.vue'
  import UnsavedChangesDialog from '@/components/ui/UnsavedChangesDialog.vue'
  import { useToast } from '@/components/ui/toast'
  import { ROUTES } from '@/router/routes'
  import NewPdfViewer from '@/components/pdf/NewPdfViewer.vue'

  const { t } = useTranslation({
    en: {
      backToTemplates: 'Back to document templates',
      backToAdminTemplates: 'Back to Dokumentenvorlagen',
      loading: 'Loading template...',
      editTemplateName: 'Edit template name',
      preview: 'Preview',
      save: 'Save',
      publish: 'Publish Template',
      draftTemplate: 'Draft',
      addFields: 'Add Fields',
      dragDropInstructions: 'Drag and drop fields onto the document',
      textField: 'Short text',
      dateField: 'Date',
      checkbox: 'Checkbox',
      signature: 'Signature',
      properties: 'Properties',
      configureField: 'Configure the selected field',
      selectFieldToEdit: 'Select a field to edit its properties',
      fieldType: 'Field Type',
      placeholder: 'Placeholder (optional)',
      required: 'Required',
      entity: 'Participant',
      tag: 'Connected data',
      requiredField: 'Mark as required',
      assignedTo: 'Assigned to',
      client: 'Client',
      opponent: 'Opponent',
      lawyer: 'Lawyer',
      text: 'Text',
      textArea: 'Long text',
      date: 'Date',
      enterPlaceholder: 'Enter placeholder text...',
      duplicate: 'Duplicate',
      delete: 'Delete',
      zoomOut: 'Zoom out',
      zoomIn: 'Zoom in',
      fitToPage: 'Fit to page',
      previousPage: 'Previous page',
      nextPage: 'Next page',
      of: 'of',
      pageOf: 'Page {current} of {total}',
      noTemplate: 'Template not found',
      errorLoading: 'Error loading template',
      errorSaving: 'Error saving template',
      templateSaved: 'Template saved successfully',
      fieldAdded: 'Field added successfully',
      fieldDeleted: 'Field deleted successfully',
      loadingPdf: 'Loading PDF...',
      pdfError: 'Error loading PDF. Please check the file and try again.',
      noPdfYet: 'No PDF uploaded yet',
      unsavedChanges: 'Unsaved Changes',
      unsavedChangesDescription:
        'You have unsaved changes that will be lost. Are you sure you want to leave?',
      discardChanges: 'Discard Changes',
      goBack: 'Go Back',
      replaceDocument: 'Replace document',
      deleteAllFields: 'Delete all fields',
      confirmDeleteAllFields: 'Delete all fields?',
      confirmDeleteAllFieldsDescription: 'Are you sure you want to delete all form fields?',
      confirmDeleteAllFieldsButton: 'Delete all fields',
      cancel: 'Cancel',
      documentReplacedSuccess: 'Document replaced successfully. Kept {count} existing fields.',
      allFieldsDeleted: 'All fields have been deleted',
      selectPdfFile: 'Please select a PDF file',
      failedToReplaceDocument: 'Failed to replace document',
      clientFields: 'Client fields',
      lawyerFields: 'Lawyer fields',
      noEntitySelected: 'No entity selected',
      selectEntityFirst: 'Select entity first',
      selectParticipant: 'Select participant...',
      selectTagFirst: 'Select tag first',
      noAutofill: 'No autofill',
      keyboardShortcuts: 'Keyboard shortcuts',
      arrowKeysToMove: 'Use arrow keys to move selected field',
      shiftKeyToLockAxis: 'Hold Shift to lock to axis',
      deleteToRemove: 'Delete key to remove field',
      noPdfTemplateAvailable: 'No PDF template available',
      uploadPdfToStart: 'Upload a PDF to start creating form fields.',
      placeholderEnterText: 'Enter text',
      placeholderEnterDate: 'Enter date',
      placeholderClickToSign: 'Click to sign'
    },
    de: {
      backToTemplates: 'Zurück zu Dokumentenvorlagen',
      backToAdminTemplates: 'Zurück zu Dokumentenvorlagen',
      loading: 'Vorlage wird geladen...',
      editTemplateName: 'Vorlagenname bearbeiten',
      preview: 'Vorschau',
      save: 'Speichern',
      publish: 'Vorlage veröffentlichen',
      draftTemplate: 'Entwurf',
      addFields: 'Felder hinzufügen',
      dragDropInstructions: 'Felder per Drag & Drop auf das Dokument ziehen',
      textField: 'Kurzer Text',
      dateField: 'Datum',
      checkbox: 'Checkbox',
      signature: 'Unterschrift',
      properties: 'Eigenschaften',
      configureField: 'Ausgewähltes Feld konfigurieren',
      selectFieldToEdit: 'Wählen Sie ein Feld aus, um seine Eigenschaften zu bearbeiten',
      fieldType: 'Feldtyp',
      placeholder: 'Platzhalter (optional)',
      required: 'Erforderlich',
      entity: 'Beteiligte',
      tag: 'Verknüpfte Daten',
      requiredField: 'Als Pflichtfeld markieren',
      assignedTo: 'Zugewiesen an',
      client: 'Mandant',
      opponent: 'Gegner',
      lawyer: 'Anwalt',
      text: 'Text',
      textArea: 'Langer Text',
      date: 'Datum',
      enterPlaceholder: 'Platzhaltertext eingeben...',
      duplicate: 'Duplizieren',
      delete: 'Löschen',
      zoomOut: 'Verkleinern',
      zoomIn: 'Vergrößern',
      fitToPage: 'An Seite anpassen',
      previousPage: 'Vorherige Seite',
      nextPage: 'Nächste Seite',
      of: 'von',
      pageOf: 'Seite {current} von {total}',
      noTemplate: 'Vorlage nicht gefunden',
      errorLoading: 'Fehler beim Laden der Vorlage',
      errorSaving: 'Fehler beim Speichern der Vorlage',
      templateSaved: 'Vorlage erfolgreich gespeichert',
      fieldAdded: 'Feld erfolgreich hinzugefügt',
      fieldDeleted: 'Feld erfolgreich gelöscht',
      loadingPdf: 'PDF wird geladen...',
      pdfError:
        'Fehler beim Laden der PDF. Bitte überprüfen Sie die Datei und versuchen Sie es erneut.',
      noPdfYet: 'Noch keine PDF hochgeladen',
      unsavedChanges: 'Nicht gespeicherte Änderungen',
      unsavedChangesDescription:
        'Sie haben nicht gespeicherte Änderungen, die verloren gehen. Möchten Sie wirklich fortfahren?',
      discardChanges: 'Änderungen verwerfen',
      goBack: 'Zurück',
      replaceDocument: 'Dokument ersetzen',
      deleteAllFields: 'Alle Felder löschen',
      confirmDeleteAllFields: 'Alle Felder löschen?',
      confirmDeleteAllFieldsDescription:
        'Sind Sie sicher, dass Sie alle Formularfelder löschen möchten?',
      confirmDeleteAllFieldsButton: 'Alle Felder löschen',
      cancel: 'Abbrechen',
      documentReplacedSuccess:
        'Dokument erfolgreich ersetzt. {count} bestehende Felder beibehalten.',
      allFieldsDeleted: 'Alle Felder wurden gelöscht',
      selectPdfFile: 'Bitte wählen Sie eine PDF-Datei aus',
      failedToReplaceDocument: 'Dokument konnte nicht ersetzt werden',
      clientFields: 'Mandantenfelder',
      lawyerFields: 'Anwaltsfelder',
      noEntitySelected: 'Keine Beteiligte ausgewählt',
      selectEntityFirst: 'Beteiligte auswählen',
      selectParticipant: 'Beteiligte auswählen...',
      selectTagFirst: 'Zuerst Tag auswählen',
      noAutofill: 'Kein Autofill',
      keyboardShortcuts: 'Tastenkürzel',
      arrowKeysToMove: 'Pfeiltasten zum Bewegen des ausgewählten Feldes',
      shiftKeyToLockAxis: 'Shift gedrückt halten um die Achse zu sperren',
      deleteToRemove: 'Entf-Taste zum Entfernen des Feldes',
      noPdfTemplateAvailable: 'Keine PDF-Vorlage verfügbar',
      uploadPdfToStart:
        'Laden Sie eine PDF hoch, um mit der Erstellung von Formularfeldern zu beginnen.',
      placeholderEnterText: 'Text eingeben',
      placeholderEnterDate: 'Datum eingeben',
      placeholderClickToSign: 'Zum Unterschreiben klicken'
    }
  })

  const fieldTypes = computed(() => [
    { id: FormFieldTypeEnum.TEXTINPUT, name: t('textField'), icon: Type },
    { id: FormFieldTypeEnum.TEXTAREA, name: t('textArea'), icon: AlignLeft },
    { id: FormFieldTypeEnum.DATEFIELD, name: t('dateField'), icon: Calendar },
    { id: FormFieldTypeEnum.CHECKBOX, name: t('checkbox'), icon: SquareCheck },
    { id: FormFieldTypeEnum.SIGNATURE, name: t('signature'), icon: PenTool }
  ])

  const route = useRoute()
  const router = useRouter()
  const templateId = route.params.uuid as string
  const { toast } = useToast()
  const { trackEvent } = useMetrics()

  const currentPage = ref(1)
  const totalPages = ref(1)

  const template = ref<PDFFormTemplate | null>(null)
  const loading = ref(true)
  const isEditingName = ref(false)
  const editedName = ref('')
  const editedNameInputRef = ref<HTMLInputElement | null>(null)
  const MAX_NAME_LENGTH = 255
  const selectedFieldUuid = ref<string | null>(null)
  const selectedField = computed(() => {
    if (!selectedFieldUuid.value || !template.value?.form_fields) return null
    return template.value.form_fields.find((f) => f.uuid === selectedFieldUuid.value) || null
  })

  const showPdfViewer = ref(false)

  const FieldComponent = FormFieldOverlayItem
  const pdfLoading = ref(true)
  const pdfError = ref<string | null>(null)
  const zoomLevel = ref(1)
  const minZoom = 0.5
  const maxZoom = 3.0
  const fitPage = ref(true)
  const pageScales = ref<Map<number, number>>(new Map())
  const pdfContainerRef = ref<HTMLElement | null>(null)
  const isManualZoom = ref(false)

  const tags = ref<GenericDataMapper[]>([])

  // Unsaved changes tracking
  const originalTemplate = ref<PDFFormTemplate | null>(null)
  const originalName = ref('')
  const showUnsavedChangesDialog = ref(false)
  const pendingNavigation = ref<{
    to: RouteLocationNormalized
    next: NavigationGuardNext
  } | null>(null)
  const guardDisabled = ref(false)

  const fileInputRef = ref<HTMLInputElement | null>(null)
  const showDeleteAllFieldsDialog = ref(false)
  const isInteractingWithPropertiesPanel = ref(false)

  const hasUnsavedChanges = computed(() => {
    if (!template.value || !originalTemplate.value) return false

    // Don't show dialog if user already chose to discard changes
    if (guardDisabled.value) return false

    if (editedName.value !== originalName.value) return true

    const originalFields = JSON.stringify(originalTemplate.value.form_fields || [])
    const currentFields = JSON.stringify(template.value.form_fields || [])
    return originalFields !== currentFields
  })

  const relationshipOptions = computed(() => [
    { value: '', title: t('noEntitySelected') },
    { value: TagRelationshipEnum.ENTITIES_CLIENT, title: t('client') },
    { value: TagRelationshipEnum.ENTITIES_OPPONENT, title: t('opponent') }
  ])

  const availableTagOptions = computed(() => {
    const entityTags = tags.value.filter(
      (tag) => tag.is_entity_field && tag.field_type !== 'select'
    )
    return entityTags.map((tag) => ({ value: tag.key, title: tag.label }))
  })

  const tagToFormFieldTypeMap: Record<string, FormFieldTypeEnum> = {
    [FieldTypeEnum.TEXTINPUT]: FormFieldTypeEnum.TEXTINPUT,
    [FieldTypeEnum.DATEFIELD]: FormFieldTypeEnum.DATEFIELD,
    [FieldTypeEnum.EMAILINPUT]: FormFieldTypeEnum.TEXTINPUT,
    [FieldTypeEnum.YESNO]: FormFieldTypeEnum.CHECKBOX,
    [FieldTypeEnum.DECIMALNUMBERINPUT]: FormFieldTypeEnum.TEXTINPUT,
    [FieldTypeEnum.TEXTAREA]: FormFieldTypeEnum.TEXTAREA
  }

  const selectedFieldTag = computed(() => {
    if (!selectedField.value?.tag) return null
    return tags.value.find((tag) => tag.key === selectedField.value?.tag)
  })

  const isFieldTypeDisabled = computed(() => {
    if (!selectedFieldTag.value) return false
    return selectedFieldTag.value.field_type in tagToFormFieldTypeMap
  })

  const nameCharacterCount = computed(() => editedName.value.length)
  const isNameNearLimit = computed(() => nameCharacterCount.value > MAX_NAME_LENGTH * 0.8)

  const pdfUrl = computed(() => {
    if (!template.value) return ''
    return template.value.processed_pdf || template.value.original_pdf
  })
  const { pdf, pages } = usePDF(pdfUrl)

  const templateFields = computed(() => {
    if (!template.value?.form_fields || !Array.isArray(template.value.form_fields)) return []
    return template.value.form_fields
  })

  const fieldsForCurrentPage = computed(() => {
    return templateFields.value.filter((field) => field.coordinates.page === currentPage.value)
  })

  const effectiveZoom = computed(() => {
    if (fitPage.value) {
      const recentPageScale = pageScales.value.get(currentPage.value)
      if (recentPageScale) {
        return recentPageScale
      }
      const firstPageScale = pageScales.value.get(1)
      return firstPageScale || zoomLevel.value
    }
    return zoomLevel.value
  })

  const displayZoom = computed(() => {
    return Math.round(effectiveZoom.value * 100)
  })

  const loadTemplate = async () => {
    try {
      loading.value = true
      const response = await PdfFormTemplatesService.pdfFormTemplatesRetrieve({ uuid: templateId })
      template.value = response as PDFFormTemplate

      if (!Array.isArray(template.value.form_fields)) {
        template.value.form_fields = []
      }

      editedName.value = template.value.name

      originalTemplate.value = JSON.parse(JSON.stringify(template.value))
      originalName.value = template.value.name

      await trackEvent(MetricEventType.PDF_FORM_TEMPLATE_EDIT_STARTED, {
        template_uuid: template.value.uuid,
        template_name: template.value.name,
        field_count: template.value.form_fields?.length || 0,
        has_pdf: !!(template.value.processed_pdf || template.value.original_pdf)
      })
    } catch (error) {
      console.error('Failed to load template:', error)
      router.push(backRoute.value)
    } finally {
      loading.value = false
    }
  }

  const saveTemplate = async () => {
    if (!template.value) return

    try {
      const response = await PdfFormTemplatesService.pdfFormTemplatesPartialUpdate({
        uuid: templateId,
        requestBody: {
          name: editedName.value,
          form_fields: template.value.form_fields
        }
      })
      template.value = response as PDFFormTemplate

      originalTemplate.value = JSON.parse(JSON.stringify(template.value))
      originalName.value = editedName.value

      await trackEvent(MetricEventType.PDF_FORM_TEMPLATE_EDIT_SAVED, {
        template_uuid: template.value.uuid,
        template_name: template.value.name,
        field_count: template.value.form_fields?.length || 0,
        has_pdf: !!(template.value.processed_pdf || template.value.original_pdf)
      })

      toast({
        title: t('templateSaved'),
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: t('errorSaving'),
        variant: 'destructive'
      })
      console.error('Failed to save template:', error)
    }
  }

  const updateField = (fieldUuid: string, updates: Partial<FormField>) => {
    if (!fieldUuid) return
    if (!template.value?.form_fields) return
    const fieldIndex = template.value.form_fields.findIndex((f) => f.uuid === fieldUuid)
    if (fieldIndex !== -1) {
      const oldField = template.value.form_fields[fieldIndex]
      const newField = {
        ...oldField,
        ...updates
      }
      template.value.form_fields[fieldIndex] = newField
    }
  }

  const removeField = (fieldUuid: string) => {
    if (!fieldUuid) return
    if (!template.value?.form_fields) return

    const fieldToRemove = template.value.form_fields.find((f) => f.uuid === fieldUuid)
    template.value.form_fields = template.value.form_fields.filter((f) => f.uuid !== fieldUuid)
    if (selectedFieldUuid.value === fieldUuid) {
      selectedFieldUuid.value = null
    }

    if (fieldToRemove && template.value) {
      trackEvent(MetricEventType.PDF_FORM_TEMPLATE_FIELD_REMOVED, {
        template_uuid: template.value.uuid,
        field_type: fieldToRemove.type,
        field_assigned_to: fieldToRemove.assigned_to,
        remaining_field_count: template.value.form_fields.length
      })
    }
  }

  const backRoute = computed(() => {
    // Determine back route based on current route path
    if (route.path.startsWith('/admin/dashboard')) {
      return ROUTES.ADMIN.PDF_FORM_TEMPLATES.PDF_FORM_TEMPLATES_LIST
    }
    return ROUTES.APP.SETTINGS.DOCUMENT_TEMPLATES
  })

  const backButtonText = computed(() => {
    if (route.path.startsWith('/admin/dashboard')) {
      return t('backToAdminTemplates')
    }
    return t('backToTemplates')
  })

  const goBack = () => {
    if (hasUnsavedChanges.value) {
      showUnsavedChangesDialog.value = true
    } else {
      router.push(backRoute.value)
    }
  }

  const handleDiscardChanges = async () => {
    showUnsavedChangesDialog.value = false
    guardDisabled.value = true

    if (template.value) {
      await trackEvent(MetricEventType.PDF_FORM_TEMPLATE_EDIT_ABANDONED, {
        template_uuid: template.value.uuid,
        template_name: template.value.name,
        field_count: template.value.form_fields?.length || 0,
        had_unsaved_changes: true
      })
    }

    if (pendingNavigation.value) {
      const { to } = pendingNavigation.value
      pendingNavigation.value = null
      router.push(to.fullPath).finally(() => {
        guardDisabled.value = false
      })
    } else {
      router.push(backRoute.value).finally(() => {
        guardDisabled.value = false
      })
    }
  }

  const handleGoBack = () => {
    showUnsavedChangesDialog.value = false
    pendingNavigation.value = null
  }

  const handleReplaceDocument = () => {
    fileInputRef.value?.click()
  }

  const handleFileChange = async (event: Event) => {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]

    if (!file) return

    if (!file.type.includes('pdf')) {
      toast({
        title: t('errorSaving'),
        description: t('selectPdfFile'),
        variant: 'destructive'
      })
      return
    }

    try {
      const response = await PdfFormTemplatesService.pdfFormTemplatesReplaceDocumentCreate({
        uuid: templateId,
        formData: {
          file: file
        }
      })

      template.value = response as PDFFormTemplate

      originalTemplate.value = JSON.parse(JSON.stringify(template.value))

      toast({
        title: t('templateSaved'),
        description: t('documentReplacedSuccess', {
          count: template.value?.form_fields?.length || 0
        }),
        variant: 'default'
      })
    } catch (error) {
      toast({
        title: t('errorSaving'),
        description: t('failedToReplaceDocument'),
        variant: 'destructive'
      })
    }

    input.value = ''
  }

  const handleDeleteAllFields = () => {
    showDeleteAllFieldsDialog.value = true
  }

  const confirmDeleteAllFields = () => {
    if (template.value) {
      template.value.form_fields = []
      selectedFieldUuid.value = null

      toast({
        title: t('fieldDeleted'),
        description: t('allFieldsDeleted'),
        variant: 'default'
      })
    }
    showDeleteAllFieldsDialog.value = false
  }

  const cancelDeleteAllFields = () => {
    showDeleteAllFieldsDialog.value = false
  }

  onBeforeRouteLeave((to, from, next) => {
    if (guardDisabled.value || !hasUnsavedChanges.value) {
      return next()
    }

    pendingNavigation.value = { to, next }
    showUnsavedChangesDialog.value = true
    return next(false)
  })

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges.value) {
      event.preventDefault()
      return ''
    }
  }

  const startEditingName = () => {
    if (isEditingName.value) return
    isEditingName.value = true
    nextTick(() => {
      const element = editedNameInputRef.value
      if (element) {
        element.textContent = editedName.value
        element.focus()
        // Select all text
        const range = document.createRange()
        range.selectNodeContents(element)
        const selection = window.getSelection()
        selection?.removeAllRanges()
        selection?.addRange(range)
      }
    })
  }

  const handleNameInput = (event: Event) => {
    const target = event.target as HTMLElement
    let newValue = target.textContent || ''

    // Enforce max length
    if (newValue.length > MAX_NAME_LENGTH) {
      newValue = newValue.substring(0, MAX_NAME_LENGTH)
      target.textContent = newValue

      // Restore cursor position to end
      const range = document.createRange()
      range.selectNodeContents(target)
      range.collapse(false)
      const selection = window.getSelection()
      selection?.removeAllRanges()
      selection?.addRange(range)
    }

    if (editedName.value !== newValue) {
      editedName.value = newValue
    }
  }

  const handleNameBlur = () => {
    // Ensure we capture the final text content before exiting edit mode
    const element = editedNameInputRef.value
    if (element) {
      editedName.value = element.textContent || ''
    }
    isEditingName.value = false
  }

  const handleNameEnter = (event: KeyboardEvent) => {
    event.preventDefault()
    // Ensure we capture the final text content before exiting edit mode
    const element = editedNameInputRef.value
    if (element) {
      editedName.value = element.textContent || ''
    }
    isEditingName.value = false
  }

  const handleNameEscape = () => {
    isEditingName.value = false
    editedName.value = originalName.value
  }

  const previewTemplate = () => {
    showPdfViewer.value = true

    if (template.value) {
      trackEvent(MetricEventType.PDF_FORM_TEMPLATE_PREVIEWED, {
        template_uuid: template.value.uuid,
        template_name: template.value.name,
        field_count: template.value.form_fields?.length || 0
      })
    }
  }

  const duplicateField = () => {
    if (selectedField.value && template.value?.form_fields) {
      const originalField = selectedField.value
      const newUuid = uuidv4()
      const newField = {
        uuid: newUuid,
        type: originalField.type,
        tag: originalField.tag,
        tag_relationship: originalField.tag_relationship,
        placeholder: originalField.placeholder,
        required: originalField.required,
        assigned_to: originalField.assigned_to,
        coordinates: {
          ...originalField.coordinates,
          x: originalField.coordinates.x + 20, // Offset the duplicate slightly
          y: originalField.coordinates.y + 20
        }
      }

      template.value.form_fields.push(newField)
      selectedFieldUuid.value = newUuid // Select the new duplicated field
    }
  }

  const deleteField = () => {
    if (selectedField.value) {
      removeField(selectedField.value.uuid!)
      selectedFieldUuid.value = null
    }
  }

  const handleKeydown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement
    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.contentEditable === 'true'

    // Only handle keys when a field is selected and not in an input field
    if (!isInputField && selectedField.value) {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault()
        deleteField()
        return
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault()

        const moveDistance = 1
        let deltaX = 0
        let deltaY = 0

        switch (event.key) {
          case 'ArrowUp':
            deltaY = -moveDistance
            break
          case 'ArrowDown':
            deltaY = moveDistance
            break
          case 'ArrowLeft':
            deltaX = -moveDistance
            break
          case 'ArrowRight':
            deltaX = moveDistance
            break
        }

        if (event.shiftKey) {
          // Lock to the primary axis of movement
          if (Math.abs(deltaX) > Math.abs(deltaY)) {
            deltaY = 0 // Lock to horizontal movement
          } else {
            deltaX = 0 // Lock to vertical movement
          }
        }

        const currentField = selectedField.value
        const newX = Math.max(0, currentField.coordinates.x + deltaX)
        const newY = Math.max(0, currentField.coordinates.y + deltaY)

        updateField(currentField.uuid!, {
          coordinates: {
            ...currentField.coordinates,
            x: newX,
            y: newY
          }
        })
      }
    }

    if (event.key === 'Escape') {
      selectedFieldUuid.value = null
    }
  }

  const handleFieldClick = (field: FormField) => {
    if (!field.uuid) return
    selectedFieldUuid.value = field.uuid
  }

  const handlePdfBackgroundClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const clickedOnField = target.closest('[data-field-overlay]') || target.closest('.vdr')

    // Only deselect if:
    // - Not clicking on a field
    // - Not interacting with properties panel
    // - Not moving or resizing a field
    if (
      !clickedOnField &&
      !isInteractingWithPropertiesPanel.value &&
      !isMovingField.value &&
      !isResizingField.value
    ) {
      selectedFieldUuid.value = null
    }
  }

  const handleFieldMove = (field: FormField, newPosition: { x: number; y: number }) => {
    const boundedX = Math.max(0, newPosition.x)
    const boundedY = Math.max(0, newPosition.y)

    updateField(field.uuid!, {
      coordinates: {
        ...field.coordinates,
        x: boundedX,
        y: boundedY
      }
    })
  }

  const handleFieldDragStart = (field: FormField) => {
    selectedFieldUuid.value = field.uuid!
    isMovingField.value = true
  }

  const handleFieldDragEnd = () => {
    isMovingField.value = false
  }

  const handleFieldResize = (field: FormField, newSize: { width: number; height: number }) => {
    updateField(field.uuid!, {
      coordinates: {
        ...field.coordinates,
        width: newSize.width,
        height: newSize.height
      }
    })
  }

  const handleFieldResizeStart = () => {
    isResizingField.value = true
  }

  const handleFieldResizeEnd = () => {
    isResizingField.value = false
  }

  const handleFieldDelete = (field: FormField) => {
    removeField(field.uuid!)
  }

  const isDraggingFromSidepanel = ref(false)
  const draggedFieldType = ref<string | null>(null)
  const draggedFieldPosition = ref<{ x: number; y: number }>({ x: 0, y: 0 })

  const isMovingField = ref(false)
  const isResizingField = ref(false)
  const dragOffset = ref<{ x: number; y: number }>({ x: 0, y: 0 })

  const onSidebarButtonMouseDown = (fieldType: any, event: MouseEvent) => {
    event.preventDefault()

    isDraggingFromSidepanel.value = true
    draggedFieldType.value = fieldType.id

    // Calculate initial offset from where user clicked on the button
    const buttonRect = (event.target as HTMLElement).closest('button')?.getBoundingClientRect()
    if (buttonRect) {
      dragOffset.value = {
        x: event.clientX - buttonRect.left,
        y: event.clientY - buttonRect.top
      }
    } else {
      // Fallback to center of preview field
      const fieldWidth = getDefaultFieldWidth(fieldType.id)
      const fieldHeight = getDefaultFieldHeight(fieldType.id)
      dragOffset.value = {
        x: fieldWidth / 2,
        y: fieldHeight / 2
      }
    }

    // Set initial position
    draggedFieldPosition.value = {
      x: event.clientX,
      y: event.clientY
    }

    // Add global mouse move and up listeners
    const handleMouseMove = (e: MouseEvent) => {
      draggedFieldPosition.value = {
        x: e.clientX,
        y: e.clientY
      }
    }

    const handleMouseUp = (e: MouseEvent) => {
      const pdfContainer =
        document.querySelector('.vue-pdf-embed') || document.querySelector('canvas')
      if (pdfContainer) {
        const containerRect = pdfContainer.getBoundingClientRect()

        if (
          e.clientX >= containerRect.left &&
          e.clientX <= containerRect.right &&
          e.clientY >= containerRect.top &&
          e.clientY <= containerRect.bottom
        ) {
          // Calculate position relative to PDF, accounting for the drag offset
          // This ensures the field is positioned so the cursor remains at the same relative position
          const pdfX = (e.clientX - containerRect.left - dragOffset.value.x) / effectiveZoom.value
          const pdfY = (e.clientY - containerRect.top - dragOffset.value.y) / effectiveZoom.value

          // Create field at the dropped position
          createFieldAtPosition(fieldType.id, Math.max(0, pdfX), Math.max(0, pdfY))
        }
      }

      isDraggingFromSidepanel.value = false
      draggedFieldType.value = null

      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const dragPreviewField = computed<FormField | null>(() => {
    if (!draggedFieldType.value) return null

    return {
      uuid: 'drag-preview',
      id: 'drag-preview',
      type: draggedFieldType.value as FormFieldTypeEnum,
      placeholder: `${draggedFieldType.value} field`,
      required: false,
      assigned_to: AssignedToEnum.CLIENT,
      coordinates: {
        x: 0,
        y: 0,
        width: getDefaultFieldWidth(draggedFieldType.value),
        height: getDefaultFieldHeight(draggedFieldType.value),
        page: currentPage.value
      }
    }
  })

  const createFieldAtPosition = (fieldType: string, x: number, y: number) => {
    const newUuid = uuidv4()

    let placeholder = ''
    if (fieldType !== 'checkbox') {
      switch (fieldType) {
        case FormFieldTypeEnum.TEXTINPUT:
          placeholder = t('placeholderEnterText')
          break
        case FormFieldTypeEnum.TEXTAREA:
          placeholder = t('placeholderEnterText')
          break
        case FormFieldTypeEnum.DATEFIELD:
          placeholder = t('placeholderEnterDate')
          break
        case FormFieldTypeEnum.SIGNATURE:
          placeholder = t('placeholderClickToSign')
          break
        default:
          placeholder = t('placeholderEnterText')
      }
    }

    const newField = {
      uuid: newUuid,
      type: fieldType as FormFieldTypeEnum,
      placeholder: placeholder,
      required: false,
      assigned_to: AssignedToEnum.CLIENT,
      coordinates: {
        x: Math.max(0, x),
        y: Math.max(0, y),
        width: getDefaultFieldWidth(fieldType),
        height: getDefaultFieldHeight(fieldType),
        page: currentPage.value
      }
    }

    if (template.value) {
      if (!Array.isArray(template.value.form_fields)) {
        template.value.form_fields = []
      }
      template.value.form_fields.push(newField as FormField)
      selectedFieldUuid.value = newUuid

      trackEvent(MetricEventType.PDF_FORM_TEMPLATE_FIELD_ADDED, {
        template_uuid: template.value.uuid,
        field_type: fieldType,
        field_assigned_to: newField.assigned_to,
        total_field_count: template.value.form_fields.length,
        page_number: currentPage.value
      })
    }
  }

  const getDefaultFieldWidth = (fieldType: string): number => {
    switch (fieldType) {
      case FormFieldTypeEnum.TEXTINPUT:
      case FormFieldTypeEnum.TEXTAREA:
        return 200
      case FormFieldTypeEnum.DATEFIELD:
        return 150
      case FormFieldTypeEnum.CHECKBOX:
        return 20
      case FormFieldTypeEnum.SIGNATURE:
        return 250
      default:
        return 150
    }
  }

  const getDefaultFieldHeight = (fieldType: string): number => {
    switch (fieldType) {
      case FormFieldTypeEnum.TEXTAREA:
        return 60
      case FormFieldTypeEnum.CHECKBOX:
        return 20
      case FormFieldTypeEnum.SIGNATURE:
        return 100
      default:
        return 30
    }
  }

  const onPdfLoaded = (viewport: any, pageNumber?: number) => {
    pdfLoading.value = false
    pdfError.value = null

    if (viewport && viewport.scale && pageNumber) {
      pageScales.value.set(pageNumber, viewport.scale)
    }

    // Update total pages if available
    if (pdf.value) {
      nextTick(() => {
        totalPages.value = pages.value || 1
      })
    }
  }

  const onPdfError = (error: any) => {
    pdfLoading.value = false
    pdfError.value = 'Failed to load PDF'
    console.error('PDF loading error:', error)
  }

  // Zoom controls
  const zoomIn = () => {
    if (fitPage.value) {
      // If currently in fit page mode, switch to manual and start from effective zoom
      zoomLevel.value = Math.min(maxZoom, effectiveZoom.value + 0.25)
      fitPage.value = false
    } else {
      zoomLevel.value = Math.min(maxZoom, zoomLevel.value + 0.25)
    }
    isManualZoom.value = true
  }

  const zoomOut = () => {
    if (fitPage.value) {
      // If currently in fit page mode, switch to manual and start from effective zoom
      zoomLevel.value = Math.max(minZoom, effectiveZoom.value - 0.25)
      fitPage.value = false
    } else {
      zoomLevel.value = Math.max(minZoom, zoomLevel.value - 0.25)
    }
    isManualZoom.value = true
  }

  const fitToPage = () => {
    nextTick(() => {
      if (pdfContainerRef.value) {
        const pdfCanvas = pdfContainerRef.value.querySelector('canvas')
        if (pdfCanvas) {
          const canvasRect = pdfCanvas.getBoundingClientRect()
          const containerRect = pdfContainerRef.value.getBoundingClientRect()

          const currentScale = pageScales.value.get(currentPage.value) || 1.0

          const originalWidth = canvasRect.width / currentScale
          const originalHeight = canvasRect.height / currentScale

          const containerWidth = containerRect.width - 48 // Account for padding
          const containerHeight = containerRect.height - 48 // Account for padding

          const scaleX = containerWidth / originalWidth
          const scaleY = containerHeight / originalHeight

          const scale = Math.min(scaleX, scaleY)
          const newScale = Math.max(0.1, Math.min(3.0, scale))

          zoomLevel.value = newScale
          fitPage.value = false
          isManualZoom.value = false
        }
      }
    })
  }

  const previousPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--
    }
  }

  const nextPage = () => {
    if (currentPage.value < totalPages.value) {
      currentPage.value++
    }
  }

  watch(
    pages,
    (newPages) => {
      if (newPages) {
        totalPages.value = newPages
      }
    },
    { immediate: true }
  )

  watch(
    [pdf, pages],
    ([newPdf, newPages]) => {
      if (newPdf && newPages && newPages > 0) {
        // PDF is available and has pages, we can show it
        pdfLoading.value = false
        pdfError.value = null
      } else if (!newPdf && pdfUrl.value) {
        // PDF is not available but we have a URL, so it's loading
        pdfLoading.value = true
        pdfError.value = null
      }
    },
    { immediate: true }
  )

  watch(
    pdfUrl,
    (newUrl, oldUrl) => {
      if (newUrl !== oldUrl) {
        pageScales.value.clear()
        if (newUrl) {
          fitPage.value = true
          pdfLoading.value = true
          pdfError.value = null
        } else {
          pdfLoading.value = false
          pdfError.value = null
        }
      }
    },
    { immediate: true }
  )

  watch(currentPage, (newPage, oldPage) => {
    if (newPage !== oldPage) {
      // Clear selected field when changing pages
      selectedFieldUuid.value = null
    }
  })

  const fetchTags = async () => {
    try {
      const result = await CasesService.casesGenericDatamappersList()
      tags.value = result.results as GenericDataMapper[]
    } catch (error) {
      console.error('Failed to fetch entity tags:', error)
    }
  }

  const updateFieldWithTagAndEntity = (field: FormField | null, updates: Partial<FormField>) => {
    if (!field) return

    const tagKey = updates.tag !== undefined ? updates.tag : field.tag
    const entityRelationship =
      updates.tag_relationship !== undefined
        ? updates.tag_relationship?.toString() || ''
        : field.tag_relationship?.toString() || ''

    let fieldType = updates.type !== undefined ? updates.type : field.type
    let newCoordinates = field.coordinates

    // Auto-set field type based on tag's field_type
    if (tagKey && updates.tag !== undefined) {
      const selectedTag = tags.value.find((tag) => tag.key === tagKey)
      if (selectedTag && selectedTag.field_type in tagToFormFieldTypeMap) {
        fieldType = tagToFormFieldTypeMap[selectedTag.field_type]

        if (fieldType === FormFieldTypeEnum.CHECKBOX) {
          newCoordinates = {
            ...field.coordinates,
            width: getDefaultFieldWidth(fieldType),
            height: getDefaultFieldHeight(fieldType)
          }
        }
      }
    }

    let placeholder = ''

    // Don't set placeholder for checkboxes
    if (fieldType !== FormFieldTypeEnum.CHECKBOX && tagKey) {
      const tagLabel = availableTagOptions.value.find((opt) => opt.value === tagKey)?.title || ''

      if (tagLabel) {
        if (entityRelationship) {
          const entityLabel =
            relationshipOptions.value.find((opt) => opt.value === entityRelationship)?.title || ''
          if (entityLabel) {
            placeholder = `${entityLabel} ${tagLabel}`
          } else {
            placeholder = tagLabel
          }
        } else {
          placeholder = tagLabel
        }
      }
    }

    updateField(field.uuid!, {
      ...updates,
      type: fieldType,
      placeholder,
      coordinates: newCoordinates
    })
  }

  onMounted(async () => {
    await Promise.all([loadTemplate(), fetchTags()])

    window.addEventListener('beforeunload', handleBeforeUnload)

    document.addEventListener('keydown', handleKeydown)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)

    document.removeEventListener('keydown', handleKeydown)
  })
</script>

<template>
  <div class="flex h-[calc(100vh-56px)] flex-col bg-gray-50">
    <!-- Header -->
    <div class="flex justify-between border-b border-gray-200 bg-white px-6 py-4">
      <div class="flex flex-col gap-2">
        <!-- Back button -->
        <div class="flex items-center gap-2">
          <button
            class="flex items-center gap-2 text-sm text-gray-600 underline transition-colors hover:text-gray-900"
            @click="goBack"
          >
            <ArrowLeft class="h-4 w-4" />
            {{ backButtonText }}
          </button>
        </div>
        <!-- Title and actions -->
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <h1
            v-if="!isEditingName"
            class="max-w-2xl cursor-pointer truncate text-2xl font-semibold text-gray-900"
            :title="editedName || t('loading')"
            @click="startEditingName"
          >
            {{ editedName || t('loading') }}
          </h1>
          <h1
            v-else
            ref="editedNameInputRef"
            contenteditable="true"
            class="max-w-2xl overflow-x-auto border-b-2 border-purple-600 text-2xl font-semibold text-gray-900
              focus:outline-none"
            @blur="handleNameBlur"
            @keydown.enter="handleNameEnter"
            @keydown.escape="handleNameEscape"
            @input="handleNameInput"
          ></h1>
          <div
            v-if="isEditingName"
            class="text-xs"
            :class="isNameNearLimit ? 'text-orange-600' : 'text-gray-500'"
          >
            {{ nameCharacterCount }} / {{ MAX_NAME_LENGTH }}
          </div>
          <Button
            variant="secondary"
            size="icon"
            class="h-6 w-6"
            @click="startEditingName"
          >
            <Pencil class="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <Button
                variant="secondary"
                size="icon"
                class="h-6 w-6"
              >
                <MoreHorizontal class="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem @click="handleReplaceDocument">
                <Replace class="mr-2 h-4 w-4" />
                {{ t('replaceDocument') }}
              </DropdownMenuItem>
              <DropdownMenuItem
                class="text-red-600"
                @click="handleDeleteAllFields"
              >
                <Trash2 class="mr-2 h-4 w-4" />
                {{ t('deleteAllFields') }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            :disabled="!pdfUrl"
            class="flex items-center gap-2"
            @click="previewTemplate"
          >
            <Eye class="h-4 w-4" />
            {{ t('preview') }}
          </Button>

          <Button
            variant="default"
            size="sm"
            class="flex items-center gap-2"
            @click="saveTemplate()"
          >
            {{ t('save') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Sidebar -->
      <div class="flex w-80 min-w-64 flex-col overflow-hidden border-r border-gray-200 bg-white">
        <!-- Add fields section -->
        <div class="flex-shrink-0 border-b border-gray-200 p-4">
          <h3 class="mb-1 text-base font-semibold text-gray-900">{{ t('addFields') }}</h3>
          <p class="mb-4 text-sm text-gray-600">
            {{ t('dragDropInstructions') }}
          </p>

          <div class="space-y-2">
            <!-- First row: Text and Text Area -->
            <div class="grid grid-cols-2 gap-2">
              <Button
                v-for="fieldType in [fieldTypes[0], fieldTypes[1]]"
                :key="fieldType.id"
                variant="secondary"
                size="sm"
                class="flex cursor-grab select-none items-center justify-start gap-1 active:cursor-grabbing"
                @mousedown="onSidebarButtonMouseDown(fieldType, $event)"
              >
                <component
                  :is="fieldType.icon"
                  class="h-5 w-5"
                />
                {{ fieldType.name }}
              </Button>
            </div>

            <!-- Second row: Date and Checkbox -->
            <div class="grid grid-cols-2 gap-2">
              <Button
                v-for="fieldType in [fieldTypes[2], fieldTypes[3]]"
                :key="fieldType.id"
                variant="secondary"
                size="sm"
                class="flex cursor-grab select-none items-center justify-start gap-1 active:cursor-grabbing"
                @mousedown="onSidebarButtonMouseDown(fieldType, $event)"
              >
                <component
                  :is="fieldType.icon"
                  class="h-5 w-5"
                />
                {{ fieldType.name }}
              </Button>
            </div>

            <!-- Third row: Signature -->
            <div class="grid grid-cols-2 gap-2">
              <Button
                :key="fieldTypes[4].id"
                variant="secondary"
                size="sm"
                class="flex cursor-grab select-none items-center justify-start gap-1 active:cursor-grabbing"
                @mousedown="onSidebarButtonMouseDown(fieldTypes[4], $event)"
              >
                <component
                  :is="fieldTypes[4].icon"
                  class="h-5 w-5"
                />
                {{ fieldTypes[4].name }}
              </Button>
            </div>
          </div>
        </div>

        <!-- Field properties section -->
        <div
          class="properties-panel flex-1 overflow-y-auto p-4"
          @mousedown="isInteractingWithPropertiesPanel = true"
          @mouseup="isInteractingWithPropertiesPanel = false"
          @focusin="isInteractingWithPropertiesPanel = true"
          @focusout="isInteractingWithPropertiesPanel = false"
        >
          <h3 class="mb-1 text-base font-semibold text-gray-900">{{ t('properties') }}</h3>
          <p class="mb-4 text-sm text-gray-600">{{ t('configureField') }}</p>

          <div
            v-if="selectedField"
            class="space-y-4 border-b border-gray-200 pb-4"
          >
            <!-- Autofill section with Tag and Entity side by side -->
            <div>
              <div class="grid grid-cols-2 gap-3">
                <!-- Tag Selection -->
                <SelectField
                  name="tag"
                  :label="t('tag')"
                  :model-value="selectedField.tag || ''"
                  :options="availableTagOptions"
                  :is-filterable="true"
                  :allow-unselect="true"
                  :placeholder="'Select tag...'"
                  :popover-align="'start'"
                  :limit-popover-width="false"
                  @update:model-value="
                    (value) => {
                      updateFieldWithTagAndEntity(selectedField, {
                        tag: value,
                        tag_relationship: value ? selectedField?.tag_relationship : '' // Clear entity when tag is cleared
                      })
                    }
                  "
                />

                <!-- Entity Selection -->
                <SelectField
                  name="entityRelationship"
                  :label="t('entity')"
                  :model-value="selectedField.tag_relationship?.toString() || ''"
                  :options="relationshipOptions"
                  :disabled="!selectedFieldTag"
                  :placeholder="selectedFieldTag ? t('selectParticipant') : t('selectTagFirst')"
                  :popover-align="'end'"
                  :limit-popover-width="false"
                  @update:model-value="
                    (value) => {
                      updateFieldWithTagAndEntity(selectedField, {
                        tag_relationship: value
                      })
                    }
                  "
                />
              </div>
            </div>

            <!-- Field type -->
            <SelectField
              name="fieldType"
              :label="t('fieldType')"
              :model-value="selectedField.type"
              :disabled="isFieldTypeDisabled"
              :options="[
                { value: FormFieldTypeEnum.TEXTINPUT, title: t('text'), icon: FileText },
                { value: FormFieldTypeEnum.TEXTAREA, title: t('textArea'), icon: AlignLeft },
                { value: FormFieldTypeEnum.DATEFIELD, title: t('date'), icon: Calendar },
                { value: FormFieldTypeEnum.CHECKBOX, title: t('checkbox'), icon: SquareCheck },
                { value: FormFieldTypeEnum.SIGNATURE, title: t('signature'), icon: PenTool }
              ]"
              @update:model-value="
                (newType) => {
                  updateField(selectedField!.uuid!, {
                    type: newType,
                    coordinates: {
                      ...selectedField!.coordinates,
                      width: getDefaultFieldWidth(newType),
                      height: getDefaultFieldHeight(newType)
                    },
                    placeholder: ''
                  })
                }
              "
            />

            <!-- Placeholder -->
            <div v-if="selectedField.type !== FormFieldTypeEnum.CHECKBOX">
              <InputField
                name="placeholder"
                :label="t('placeholder')"
                :model-value="selectedField.placeholder || ''"
                :placeholder="t('enterPlaceholder')"
                @update:model-value="updateField(selectedField!.uuid!, { placeholder: $event })"
              />
            </div>

            <!-- Assigned to -->
            <SelectField
              name="assignedTo"
              :label="t('assignedTo')"
              :model-value="selectedField.assigned_to"
              :options="[
                { value: AssignedToEnum.CLIENT, title: t('client'), icon: User },
                { value: AssignedToEnum.LAWYER, title: t('lawyer'), icon: Scale }
              ]"
              @update:model-value="updateField(selectedField!.uuid!, { assigned_to: $event })"
            />

            <!-- Required checkbox -->
            <div class="flex items-center">
              <input
                id="required"
                :checked="selectedField.required"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                @change="
                  updateField(selectedField!.uuid!, {
                    required: ($event.target as HTMLInputElement).checked
                  })
                "
              />
              <label
                for="required"
                class="ml-2 block text-sm text-gray-900"
              >
                {{ t('requiredField') }}
              </label>
            </div>

            <!-- Action buttons -->
            <div class="flex w-full gap-2 pt-4">
              <Button
                variant="secondary"
                class="flex flex-1 items-center gap-1"
                @click="duplicateField"
              >
                <Copy class="h-4 w-4" />
                {{ t('duplicate') }}
              </Button>
              <Button
                variant="secondary"
                class="flex flex-1 items-center gap-1 text-red-600"
                @click="deleteField"
              >
                <Trash class="h-4 w-4" />
                {{ t('delete') }}
              </Button>
            </div>
          </div>

          <div
            v-else
            class="py-8 text-center text-gray-500"
          >
            <FileText class="mx-auto mb-2 h-12 w-12 text-gray-300" />
            <p class="text-sm">{{ t('selectFieldToEdit') }}</p>
          </div>
        </div>
      </div>

      <!-- PDF Viewer -->
      <div class="relative flex-1 overflow-x-hidden">
        <!-- Field Legend -->
        <div
          v-if="fieldsForCurrentPage.length > 0"
          class="absolute right-4 top-20 z-40 rounded-lg border border-gray-200 bg-white/95 p-4 shadow-lg
            backdrop-blur-sm"
        >
          <div class="space-y-2">
            <div class="flex items-center gap-3 text-sm">
              <div class="h-4 w-4 rounded border-2 border-blue-500 bg-blue-100"></div>
              <span class="text-gray-700">{{ t('lawyerFields') }}</span>
            </div>
            <div class="flex items-center gap-3 text-sm">
              <div class="h-4 w-4 rounded border-2 border-purple-500 bg-purple-100"></div>
              <span class="text-gray-700">{{ t('clientFields') }}</span>
            </div>
          </div>
        </div>

        <div
          v-if="loading"
          class="flex h-full items-center justify-center"
        >
          <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
        </div>

        <div
          v-else-if="pdfUrl"
          class="relative h-full bg-white"
        >
          <!-- PDF viewer content with zoom controls -->
          <div class="flex h-full flex-col">
            <!-- Zoom controls -->
            <div class="flex h-16 items-center justify-between border-b border-gray-200 px-4">
              <div class="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  :disabled="!fitPage && zoomLevel <= minZoom"
                  @click="zoomOut"
                >
                  <ZoomOut class="h-4 w-4" />
                </Button>
                <span class="text-sm font-medium">{{ displayZoom }}%</span>
                <Button
                  variant="secondary"
                  size="sm"
                  :disabled="!fitPage && zoomLevel >= maxZoom"
                  @click="zoomIn"
                >
                  <ZoomIn class="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  class="flex items-center gap-1"
                  :disabled="!isManualZoom"
                  @click="fitToPage"
                >
                  <FileText class="h-4 w-4" />
                  {{ t('fitToPage') }}
                </Button>
              </div>

              <!-- Page navigation -->
              <div class="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  :disabled="currentPage <= 1"
                  @click="previousPage"
                >
                  <ChevronLeft class="h-4 w-4" />
                </Button>
                <div class="flex items-center gap-1">
                  <span
                    class="rounded border border-gray-300 bg-white px-2 py-1 text-sm font-medium"
                  >
                    {{ currentPage }}
                  </span>
                  <span class="text-sm text-gray-500">{{ t('of') }}</span>
                  <span class="text-sm text-gray-500">{{ pages || 0 }}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  :disabled="currentPage >= (pages || 0)"
                  @click="nextPage"
                >
                  <ChevronRight class="h-4 w-4" />
                </Button>
              </div>
            </div>

            <!-- PDF content area -->
            <div
              ref="pdfContainerRef"
              class="flex-1 overflow-auto p-6"
            >
              <div class="mx-auto max-w-4xl">
                <!-- PDF with overlay fields -->
                <div class="relative">
                  <!-- Loading state -->
                  <div
                    v-if="loading"
                    class="flex min-h-[800px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
                  >
                    <div class="text-center">
                      <div
                        class="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"
                      ></div>
                      <p class="text-gray-500">Loading template...</p>
                    </div>
                  </div>

                  <!-- PDF Loading state -->
                  <div
                    v-else-if="pdfLoading && pdfUrl"
                    class="flex min-h-[800px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
                  >
                    <div class="text-center">
                      <div
                        class="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"
                      ></div>
                      <p class="text-gray-500">Loading PDF...</p>
                    </div>
                  </div>

                  <!-- PDF error state -->
                  <div
                    v-else-if="pdfError"
                    class="flex min-h-[800px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
                  >
                    <div class="text-center">
                      <p class="mb-2 text-red-500">{{ pdfError }}</p>
                      <p class="text-sm text-gray-500">Please check the PDF file and try again.</p>
                    </div>
                  </div>

                  <!-- PDF content -->
                  <div
                    v-else-if="pdf && pdfUrl"
                    class="relative w-fit overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                    @click="handlePdfBackgroundClick"
                  >
                    <VuePDF
                      :pdf="pdf"
                      :page="currentPage"
                      :scale="effectiveZoom"
                      class="pdf-no-select w-full"
                      @loaded="(viewport) => onPdfLoaded(viewport, currentPage)"
                      @error="onPdfError"
                    />

                    <!-- Form field overlays for current page -->
                    <FieldComponent
                      v-for="field in fieldsForCurrentPage"
                      :key="`${field.uuid}-${effectiveZoom}`"
                      :field="field"
                      :zoom-level="effectiveZoom"
                      :is-selected="selectedField?.uuid === field.uuid"
                      :is-editable="true"
                      @click="handleFieldClick"
                      @move="handleFieldMove"
                      @resize="handleFieldResize"
                      @resizestart="handleFieldResizeStart"
                      @resizeend="handleFieldResizeEnd"
                      @delete="handleFieldDelete"
                      @dragstart="handleFieldDragStart"
                      @dragend="handleFieldDragEnd"
                    />
                  </div>

                  <!-- No PDF state -->
                  <div
                    v-else
                    class="flex min-h-[800px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-lg"
                  >
                    <div class="text-center text-gray-500">
                      <FileText class="mx-auto mb-4 h-12 w-12 text-gray-300" />
                      <p>{{ t('noPdfTemplateAvailable') }}</p>
                      <p class="text-sm">{{ t('uploadPdfToStart') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating draggable field preview -->
    <div
      v-if="isDraggingFromSidepanel && dragPreviewField"
      class="pointer-events-none fixed z-50"
      :style="{
        left: `${draggedFieldPosition.x - dragOffset.x}px`,
        top: `${draggedFieldPosition.y - dragOffset.y}px`
      }"
    >
      <div class="relative opacity-80 shadow-lg">
        <FormFieldOverlayItem
          :field="dragPreviewField"
          :zoom-level="effectiveZoom"
          :is-selected="false"
          :is-editable="false"
          class="pointer-events-none"
        />
        <!-- Overlay to show it's a preview -->
        <div
          class="pointer-events-none absolute inset-0 rounded border-2 border-dashed border-blue-500 bg-blue-100
            bg-opacity-30"
        ></div>
      </div>
    </div>
  </div>

  <!-- Hidden file input for document replacement -->
  <input
    ref="fileInputRef"
    type="file"
    accept=".pdf"
    class="hidden"
    @change="handleFileChange"
  />

  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog
    :open="showUnsavedChangesDialog"
    :title="t('unsavedChanges')"
    :description="t('unsavedChangesDescription')"
    :confirm-text="t('discardChanges')"
    :cancel-text="t('goBack')"
    @update:open="showUnsavedChangesDialog = $event"
    @confirm="handleDiscardChanges"
    @cancel="handleGoBack"
  />

  <!-- Delete All Fields Confirmation Dialog -->
  <UnsavedChangesDialog
    :open="showDeleteAllFieldsDialog"
    :title="t('confirmDeleteAllFields')"
    :description="t('confirmDeleteAllFieldsDescription')"
    :confirm-text="t('confirmDeleteAllFieldsButton')"
    :cancel-text="t('cancel')"
    @update:open="showDeleteAllFieldsDialog = $event"
    @confirm="confirmDeleteAllFields"
    @cancel="cancelDeleteAllFields"
  />

  <!-- PDF Preview Modal -->
  <NewPdfViewer
    v-model:isOpen="showPdfViewer"
    :url="template?.processed_pdf || template?.original_pdf || ''"
    :filename="template?.name || 'PDF Template'"
    :initial-page="1"
    :annotation="false"
    :form-fields="template?.form_fields || []"
    :show-form-fields="true"
    :disable-edit-page="true"
    :hide-topbar="true"
  />
</template>

<style scoped>
  /* Custom styles for field overlays and drag/drop */
  .field-overlay {
    transition: all 0.2s ease;
  }

  .field-overlay:hover {
    transform: scale(1.02);
  }

  .field-overlay.selected {
    box-shadow: 0 0 0 2px rgb(59 130 246);
  }

  /* Disable text selection on PDF */
  .pdf-no-select {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .pdf-no-select * {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }
</style>
